import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import useSessionStorageState from '../../../hooks/useSessionStorageState';
import BuzzAutoCompleteSingleSelectInput from '../../../components/BuzzAutoCompleteInput/BuzzAutoCompleteSingleSelectInput';
import CustomForm from '../../../components/FormRenderer/CustomForm';
import {
  initializeFormData,
  sanitizePayload,
} from '../../../components/FormRenderer/utils';
import CurriculumFrameworkPicker, {
  CURRICULUM_FRAMEWORK_STORAGE_KEY,
  FrameworkRequiredNotice,
} from './CurriculumFrameworkPicker';
import CurriculumBulkImport from './CurriculumBulkImport';
import {
  createCurriculumActionSuggestion,
  getCurriculumActionSuggestions,
  getCurriculumActionSuggestionsImportTemplate,
  getCurriculumAgeGroups,
  getCurriculumNodes,
  importCurriculumActionSuggestions,
  updateCurriculumActionSuggestion,
} from '../AkademySlice';

/**
 * `GET action-suggestions` accepts either `nodeId` (one node's suggestions) or
 * `frameworkId` (every suggestion across that framework's nodes, resolved server-side) —
 * at least one is required, since a suggestion carries no `frameworkId` of its own. This
 * screen fetches by `frameworkId` to show everything by default in a single request, then
 * narrows client-side to one node when the node picker is used as a filter.
 *
 * The page-level node picker is only a *filter* over that list (narrows the table to one
 * node's suggestions) — it is not where a new suggestion's node comes from, and it isn't a
 * gate on viewing or creating anything. A brand-new node with no suggestions yet wouldn't
 * even appear in that filter, so the create form has its own Node field instead, drawing
 * from every node in the framework.
 *
 * A node may carry many suggestions, optionally narrowed to an age group; `{nodeId,
 * ageGroupId}` is a lookup index, not a unique one.
 */
function AkademyCurriculumActionSuggestions() {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast } = useToast();
  const { handleLoader } = useLoader();

  const [frameworkId, setFrameworkId] = useSessionStorageState(
    CURRICULUM_FRAMEWORK_STORAGE_KEY,
    null
  );
  const [nodeId, setNodeId] = useState(null);
  const [allSuggestions, setAllSuggestions] = useState([]);
  // Which node an *edit*'s post-save refresh should target — independent of the `nodeId`
  // filter, since editing a row surfaced while viewing "all nodes" must refresh that row's
  // own node, not whatever (if anything) the filter currently points at. A create doesn't
  // need this: its target node comes straight from the form's own Node field instead.
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [showFormModal, setFormModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  const { curriculumNodes, curriculumAgeGroups } = useSelector(
    ({ akademyReducer }) => {
      return {
        curriculumNodes: akademyReducer.curriculumNodes,
        curriculumAgeGroups: akademyReducer.curriculumAgeGroups,
      };
    }
  );

  // Also used to re-fetch everything after a bulk import, since that can touch any number
  // of nodes at once — unlike a single create/edit, there's no one node to narrow to.
  const refreshAllSuggestions = () => {
    handleLoader(true);
    dispatch(getCurriculumActionSuggestions({ frameworkId }))
      .unwrap()
      .then((suggestions) => setAllSuggestions(suggestions))
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      })
      .finally(() => handleLoader(false));
  };

  useEffect(() => {
    if (!frameworkId) return;

    setNodeId(null);
    setAllSuggestions([]);
    dispatch(getCurriculumAgeGroups({ frameworkId }));
    dispatch(getCurriculumNodes({ frameworkId }));
    refreshAllSuggestions();
  }, [frameworkId]);

  // `ancestorPath` is a resolved, root-first copy of the ancestor chain kept on the node
  // itself, so the full breadcrumb in these options costs no extra fetch.
  const breadcrumbFor = (node) => {
    const path = [
      ...(node.ancestorPath || []).map(({ value }) => value),
      node.value,
    ];
    return path.join(' → ');
  };

  // Every node in the framework — the pool a *new* suggestion can target, since a node
  // with no suggestions yet is exactly who this is for.
  const allNodeOptions = curriculumNodes.map((node) => {
    return {
      label: `L${node.level} — ${breadcrumbFor(node)}`,
      value: node._id,
    };
  });

  // Only nodes that actually have a suggestion showing in the table — picking any other
  // node as a *filter* would just land on an empty list, so it's not offered there.
  const nodeIdsWithSuggestions = new Set(
    allSuggestions.map(({ nodeId: suggestionNodeId }) => suggestionNodeId)
  );
  const nodeOptions = allNodeOptions.filter(({ value }) =>
    nodeIdsWithSuggestions.has(value)
  );

  const displayedSuggestions = nodeId
    ? allSuggestions.filter(
        ({ nodeId: suggestionNodeId }) => suggestionNodeId === nodeId
      )
    : allSuggestions;

  // `nodeId` and `ageGroupId` are create-only — `UpdateCurriculumActionSuggestionDTO`
  // takes only the two text fields, so re-pointing a suggestion means creating a new one.
  const buildFormTemplate = (mode) => {
    const template = [
      {
        id: 'form.positiveRelationship',
        title: 'Positive Relationship (suggestion for parents)',
        slug: 'positiveRelationship',
        type: 'String',
      },
      {
        id: 'form.enablingEnvironment',
        title: 'Enabling Environment (suggestion for teachers/school)',
        slug: 'enablingEnvironment',
        type: 'String',
      },
    ];

    if (mode === 'CREATE') {
      template.unshift({
        id: 'form.node',
        title: 'Node',
        slug: 'nodeId',
        type: 'AutoCompleteSelect',
        options: allNodeOptions,
        required: true,
      });
      template.push({
        id: 'form.ageGroup',
        title: 'Age Group (None = applies to every age)',
        slug: 'ageGroupId',
        type: 'Select',
        options: [
          { label: 'None', value: null },
          ...curriculumAgeGroups.map(({ _id, minMonth, maxMonth }) => {
            return { label: `${minMonth}–${maxMonth} months`, value: _id };
          }),
        ],
      });
    }

    return template;
  };

  const suggestionColumns = [
    // { Header: 'Suggestion Id', accessor: '_id' },
    {
      Header: 'Node',
      Cell: (val) => {
        const { nodeId: suggestionNodeId } = val.cell.row.original;
        const node = curriculumNodes.find(
          ({ _id }) => _id === suggestionNodeId
        );
        return <>{node ? breadcrumbFor(node) : '—'}</>;
      },
    },
    { Header: 'Positive Relationship', accessor: 'positiveRelationship' },
    { Header: 'Enabling Environment', accessor: 'enablingEnvironment' },
    {
      Header: 'Age Group',
      Cell: (val) => {
        const { ageGroupId } = val.cell.row.original;
        const ageGroup = curriculumAgeGroups.find(
          ({ _id }) => _id === ageGroupId
        );

        return (
          <>{ageGroup ? `${ageGroup.minMonth}–${ageGroup.maxMonth} mo` : '—'}</>
        );
      },
    },
    {
      Header: 'Action',
      Cell: (val) => {
        const suggestion = val.cell.row.original;

        return (
          <>
            <IconButton onClick={() => handleEdit(suggestion)}>
              <EditIcon color="secondary" />
            </IconButton>
            <Tooltip title="Curriculum has no delete — deprecate instead">
              <IconButton disabled>
                <DeleteIcon
                  color="primary"
                  style={{ color: 'grey', opacity: 0.5 }}
                />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const openFormModal = () => setFormModal(true);
  const closeFormModal = () => setFormModal(false);

  const handleEdit = (suggestion) => {
    setSelectedRowId(suggestion._id);
    setEditingNodeId(suggestion.nodeId);
    setFormTemplate(buildFormTemplate('EDIT'));
    setFormData({
      _id: suggestion._id,
      positiveRelationship: suggestion.positiveRelationship || null,
      enablingEnvironment: suggestion.enablingEnvironment || null,
    });
    openFormModal();
  };

  const handleCreateNew = () => {
    setSelectedRowId(null);
    const template = buildFormTemplate('CREATE');
    setFormTemplate(template);
    setFormData(initializeFormData(template));
    openFormModal();
  };

  const handleSave = async () => {
    handleLoader(true);

    // On create, the target node comes from the form's own Node field — the user may be
    // adding the very first suggestion for a node, which is exactly why it isn't required
    // to already be selected in the page-level filter. On edit, the row being edited fixes
    // it (`editingNodeId`), since `UpdateCurriculumActionSuggestionDTO` doesn't carry nodeId.
    const targetNodeId = selectedRowId === null ? formData.nodeId : editingNodeId;

    dispatch(
      selectedRowId === null
        ? createCurriculumActionSuggestion(sanitizePayload(formData))
        : updateCurriculumActionSuggestion(formData)
    )
      .unwrap()
      .then(() => {
        SuccessToast(
          selectedRowId === null ? 'Suggestion created' : 'Suggestion saved'
        );
        return dispatch(
          getCurriculumActionSuggestions({ nodeId: targetNodeId })
        ).unwrap();
      })
      .then((suggestions) => {
        setAllSuggestions((state) => [
          ...state.filter(({ nodeId: n }) => n !== targetNodeId),
          ...suggestions,
        ]);
        closeFormModal();
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  return (
    <Box>
      <Box style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
        <Button
          onClick={handleCreateNew}
          variant={'contained'}
          color="primary"
          disabled={!frameworkId}>
          Add Suggestion
        </Button>
        <Box style={{ marginLeft: 'auto' }}>
          <CurriculumFrameworkPicker
            value={frameworkId}
            onChange={setFrameworkId}
          />
        </Box>
      </Box>

      <Box mt={2} display="flex" justifyContent="flex-end">
        <CurriculumBulkImport
          frameworkId={frameworkId}
          label="Suggestions"
          templateFilename="curriculum-suggestions-import-template.xlsx"
          getTemplate={getCurriculumActionSuggestionsImportTemplate}
          importAction={importCurriculumActionSuggestions}
          onImported={refreshAllSuggestions}
        />
      </Box>

      <Box mt={2} style={{ maxWidth: '360px' }}>
        <Typography color="textSecondary" style={{ fontSize: '0.85rem' }}>
          Node
        </Typography>
        <BuzzAutoCompleteSingleSelectInput
          size="small"
          placeholder="All nodes"
          disabled={!frameworkId}
          options={nodeOptions}
          value={nodeId}
          onChange={(value) => setNodeId(value)}
        />
      </Box>

      {!frameworkId ? (
        <FrameworkRequiredNotice>
          Pick a framework to see its suggestions.
        </FrameworkRequiredNotice>
      ) : (
        <Box my={2}>
          <BuzzTable
            columns={suggestionColumns}
            data={displayedSuggestions}
            isHandlePagination={true}
          />
          {displayedSuggestions.length === 0 && (
            <Box my={2} display="flex" justifyContent="center">
              <Typography color="textSecondary">
                {nodeId
                  ? 'No suggestions on this node yet.'
                  : 'No suggestions in this framework yet.'}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Modal open={showFormModal} onClose={closeFormModal}>
        <CustomForm
          formFor={'Action Suggestion'}
          formTemplate={formTemplate}
          formData={formData}
          setFormData={(slug, newValue) => {
            setFormData((state) => {
              state[slug] = newValue;
              return state;
            });
            setFormTemplate((state) => {
              return JSON.parse(JSON.stringify(state));
            });
          }}
          mode={selectedRowId === null ? 'CREATE' : 'EDIT'}
          handleSave={handleSave}
        />
      </Modal>
    </Box>
  );
}

export default AkademyCurriculumActionSuggestions;
